/**
 * Task-uri pe departamente — datele ecranului (plan: docs/superpowers/plans/2026-10-12-taskuri-pe-departamente-plan.md).
 *
 * Tipurile oglindesc raspunsurile din `apps/api/src/modules/tasks/taskuri.routes.ts`. Regulile
 * (cine muta ce) NU se repeta aici: fiecare card vine cu `actiuni`, lista a ce poate face omul
 * acum, calculata de server. Ecranul doar arata butoanele care chiar merg.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { arataDublura } from '../../stores/dublura'
import { toast } from 'sonner'
import type { ActiuneTask, DepartamentSlug, StareTask } from '@b4l/shared'
import { api } from '../../lib/api'

export interface Persoana {
  id: number
  name: string
}

export interface CardTask {
  id: number
  titlu: string
  stare: StareTask
  departament: DepartamentSlug | null
  /** Departamentul care a cerut, cand e altul decat cel care lucreaza. */
  sursa: DepartamentSlug | null
  /** Oamenii pe task, in ordinea in care au intrat (19.10.2026: pot fi mai multi). */
  responsabili: Persoana[]
  creatDe: Persoana
  termen: string | null
  urgent: boolean
  source: string
  asteaptaPe: string | null
  revinePe: string | null
  client: string | null
  comentarii: number
  atasamente: number
  creat: string
  /** De cand e in coloana lui (ultima schimbare de stare). */
  inColoanaDin: string
  inchis: string | null
  actiuni: ActiuneTask[]
  poateEdita: boolean
  /** Poate pune și scoate oameni de pe task (decide serverul). */
  poateOameni: boolean
  /** Poate scoate pe altii de pe task (adminul; la privat, cine l-a dat). */
  poateScoateAltii: boolean
  /** Un privat pe care îl poate trimite mai departe. */
  poateTrimite: boolean
  /** Îl văd doar cei de pe el și cine l-a dat — nici adminii (19.10.2026). */
  privat: boolean
  /** Cine l-a dat — îl vede și la el se întoarce. Gol la cele venite din email (contact@). */
  privatDe: Persoana | null
  /** La un privat venit din email: unde l-ar trimite filtrul („Trimite la Vânzări"). */
  departamentPropus: DepartamentSlug | null
  /** Trimis deodată la mai multe departamente: câte task-uri are grupul, cu acesta (1 = singur). */
  grup: number
}

/**
 * Ce e selectat sus: un departament, „trimise" (cererile noastre la altii), „toate" (admin) sau
 * „ale-mele" (19.10.2026: ce am pe mine, oriunde, plus privatele date de mine altora).
 */
export type Scop = DepartamentSlug | 'trimise' | 'toate' | 'ale-mele'

export interface SumarDepartament {
  dePreluat: number
  inLucru: number
  asteapta: number
  deschise: number
  depasite: number
}

export interface Departamente {
  departamentulMeu: DepartamentSlug | null
  departamente: (SumarDepartament & { slug: DepartamentSlug; nume: string })[]
  trimise: SumarDepartament | null
  /** Cardul „Ale mele" — doar cifrele omului; nimeni altcineva nu le vede. */
  aleMele: SumarDepartament & { private: number }
  /** Cine urmareste privatele (dreptul personal al lui Victor) — se spune la „Cine il vede". */
  urmarescPrivatele: Persoana[]
}

/** Esti pe task? */
export const esteAlMeu = (t: Pick<CardTask, 'responsabili'>, id: number | undefined) =>
  id != null && t.responsabili.some((r) => r.id === id)

/** „Adi”, „Adi și Ioana”, „Adi, Ioana și Raluca”. */
export function numeOameni(oameni: { name: string }[]): string {
  const n = oameni.map((o) => o.name)
  if (n.length <= 1) return n[0] ?? ''
  return `${n.slice(0, -1).join(', ')} și ${n[n.length - 1]}`
}

export const CHEI = {
  tot: ['taskuri'] as const,
  tablou: (scop: Scop | undefined) => ['taskuri', 'tablou', scop ?? 'implicit'] as const,
  departamente: ['taskuri', 'departamente'] as const,
  task: (id: number) => ['taskuri', 'task', id] as const,
  timeline: (id: number) => ['taskuri', 'task', id, 'timeline'] as const,
  atasamente: (id: number) => ['taskuri', 'task', id, 'atasamente'] as const,
  activitate: (scop: Scop | undefined) => ['taskuri', 'activitate', scop ?? 'implicit'] as const,
  arhiva: (p: Record<string, unknown>) => ['taskuri', 'arhiva', p] as const,
  ansamblu: ['taskuri', 'ansamblu'] as const,
  /** Insigna din bara laterala si cifra din Dashboard. */
  dePreluat: ['taskuri', 'de-preluat'] as const,
}

export function useDepartamente() {
  return useQuery<Departamente>({
    queryKey: CHEI.departamente,
    queryFn: async () => (await api.get('/tasks/departamente')).data,
  })
}

/**
 * Cine urmareste privatele, ca text („Victor Ciortan"), pentru „Cine il vede". Gol cand nu
 * urmareste nimeni. Din cifrele de sus, deja incarcate pe pagina.
 *
 * `numeFara`: doar cei care nu sunt deja numiti in fraza (Madalin, 22.10.2026: „il vad doar
 * Madalin Popa si Victor Ciortan si Victor Ciortan" — Victor daduse task-ul si urmareste
 * privatele). Gol cand toti sunt deja acolo.
 */
export function useUrmarescPrivatele(): {
  nume: string
  ids: number[]
  numeFara: (dejaNumiti: (number | null | undefined)[]) => string
} {
  const q = useDepartamente()
  const l = q.data?.urmarescPrivatele ?? []
  return {
    nume: numeOameni(l),
    ids: l.map((o) => o.id),
    numeFara: (dejaNumiti) => numeOameni(l.filter((o) => !dejaNumiti.includes(o.id))),
  }
}

export function useTablou(scop: Scop | undefined, activ = true) {
  return useQuery<{ departament: Scop; taskuri: CardTask[] }>({
    queryKey: CHEI.tablou(scop),
    queryFn: async () =>
      (await api.get('/tasks/tablou', { params: scop ? { dep: scop } : {} })).data,
    enabled: activ,
    // Colegii muta si ei carduri: tabloul se improspateaza singur cat e deschis.
    refetchInterval: 60_000,
  })
}

/** Mesajul serverului, cand exista — el spune exact de ce nu („L-a preluat deja Ana"). */
export function mesajEroare(e: unknown, implicit: string): string {
  const r = (e as { response?: { data?: { error?: { code?: string; message?: string } } } })
    ?.response
  // Validarea Zod raspunde in engleza („Invalid request payload") — omul primeste mesajul nostru.
  if (!r?.data?.error?.message || r.data.error.code === 'VALIDATION') return implicit
  return r.data.error.message
}

/** Dupa orice schimbare: tot ce tine de task-uri se reciteste (tablou, cifre, panou, insigna). */
export function useReimprospateaza() {
  const qc = useQueryClient()
  return () => void qc.invalidateQueries({ queryKey: CHEI.tot })
}

export interface DateActiune {
  asteaptaPe?: string | null
  revinePe?: string | null
  motiv?: string | null
}

export function useActiune() {
  const reimprospateaza = useReimprospateaza()
  return useMutation({
    mutationFn: async (v: { id: number; actiune: ActiuneTask } & DateActiune) => {
      const { id, ...corp } = v
      return (await api.post(`/tasks/${id}/actiune`, corp)).data
    },
    onSettled: reimprospateaza,
    onError: (e) => toast.error(mesajEroare(e, 'Nu s-a putut muta task-ul.')),
  })
}

export interface TaskNou {
  departament?: DepartamentSlug
  /** Mai multe deodata (16.10.2026; pentru oricine din 19.10): cate un task la fiecare, legate. */
  departamente?: DepartamentSlug[]
  /** Oamenii pusi direct pe task: intra „În lucru" la ei (19.10.2026, oricine, oricui). */
  oameni?: number[]
  /** Il vad doar `oameni` si cel care il creeaza. Cere oameni. */
  privat?: boolean
  titlu: string
  descriere?: string | null
  termen?: string | null
  urgent?: boolean
  clientLabel?: string | null
  /** Mailul din care vine (webmail): serverul îl recunoaște dacă mai are un task. */
  dinMail?: { messageId: string; subiect: string; expeditor: string }
}

export function useCreeaza() {
  const reimprospateaza = useReimprospateaza()
  return useMutation({
    mutationFn: async (t: TaskNou) =>
      (await api.post('/tasks', t)).data as { task: { id: number }; taskuri: { id: number }[] },
    onSuccess: reimprospateaza,
    onError: (e, t) => {
      // Task foarte asemanator deja deschis: fereastra cu „Mergi la task”, nu un toast.
      if (arataDublura(e, [t.titlu, t.descriere].filter(Boolean).join('\n\n'))) return
      toast.error(mesajEroare(e, 'Task-ul nu s-a putut salva.'))
    },
  })
}

export function useTrimite() {
  const reimprospateaza = useReimprospateaza()
  return useMutation({
    mutationFn: async (v: { id: number; departamente: DepartamentSlug[]; motiv?: string | null }) =>
      (await api.post(`/tasks/${v.id}/trimite`, { departamente: v.departamente, motiv: v.motiv }))
        .data,
    onSuccess: reimprospateaza,
    onError: (e) => {
      if (arataDublura(e)) return
      toast.error(mesajEroare(e, 'Nu s-a putut trimite.'))
    },
  })
}

/** Pune si scoate oameni de pe task (19.10.2026). */
export function useResponsabili() {
  const reimprospateaza = useReimprospateaza()
  return useMutation({
    mutationFn: async (v: { id: number; adauga?: number[]; scoate?: number[] }) =>
      (await api.post(`/tasks/${v.id}/responsabili`, { adauga: v.adauga, scoate: v.scoate })).data,
    onSuccess: reimprospateaza,
    onError: (e) => {
      if (arataDublura(e)) return
      toast.error(mesajEroare(e, 'Nu s-au putut schimba colegii de pe task.'))
    },
  })
}

/** Un task privat trimis mai departe: la alti oameni (ramane privat) sau la departament(e). */
export function useMaiDeparte() {
  const reimprospateaza = useReimprospateaza()
  return useMutation({
    mutationFn: async (v: {
      id: number
      privat: boolean
      oameni?: number[]
      departamente?: DepartamentSlug[]
      motiv?: string | null
    }) => {
      const { id, ...corp } = v
      return (await api.post(`/tasks/${id}/mai-departe`, corp)).data
    },
    onSuccess: reimprospateaza,
    onError: (e) => {
      if (arataDublura(e)) return
      toast.error(mesajEroare(e, 'Nu s-a putut trimite.'))
    },
  })
}

export function useCere() {
  const reimprospateaza = useReimprospateaza()
  return useMutation({
    mutationFn: async (v: { id: number } & Omit<TaskNou, 'clientLabel'>) => {
      const { id, ...corp } = v
      return (await api.post(`/tasks/${id}/cere`, corp)).data
    },
    onSuccess: reimprospateaza,
    onError: (e, v) => {
      if (arataDublura(e, [v.titlu, v.descriere].filter(Boolean).join('\n\n'))) return
      toast.error(mesajEroare(e, 'Cererea nu s-a putut trimite.'))
    },
  })
}

export function useEditeaza(id: number) {
  const reimprospateaza = useReimprospateaza()
  return useMutation({
    mutationFn: async (
      date: Partial<{
        titlu: string
        descriere: string | null
        termen: string | null
        urgent: boolean
      }>,
    ) => (await api.patch(`/tasks/${id}`, date)).data,
    onSuccess: reimprospateaza,
    onError: (e) => toast.error(mesajEroare(e, 'Nu s-a putut salva.')),
  })
}

// ─── Timp ────────────────────────────────────────────────────────────────────

const ZI = 86_400_000
const miezulNoptii = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()

/** Cate zile calendaristice de azi pana la data data (negativ = in trecut). */
export function zileDeAzi(iso: string): number {
  return Math.round((miezulNoptii(new Date(iso)) - miezulNoptii(new Date())) / ZI)
}

export const ziScurt = (iso: string) =>
  new Date(iso).toLocaleDateString('ro-RO', { day: 'numeric', month: 'short' })

export const oraScurt = (iso: string) =>
  new Date(iso).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })

/** Un termen e depasit doar pentru munca deschisa. */
export const depasit = (t: Pick<CardTask, 'termen' | 'stare'>) =>
  !!t.termen && t.stare !== 'done' && t.stare !== 'cancelled' && zileDeAzi(t.termen) < 0

/** „azi", „1 zi", „4 zile". */
export function zileText(n: number): string {
  return n <= 0 ? 'azi' : n === 1 ? '1 zi' : `${n} zile`
}

/** Data de azi ca `yyyy-mm-dd`, pentru campurile `<input type="date">`. */
export function aziIso(plusZile = 0): string {
  const d = new Date(Date.now() + plusZile * ZI)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
